import React, {useEffect, useState} from 'react';
import styles from './AdminProjectInfo.module.css';
import TextCard from "../../common/textCard/TextCard";
import {AdminProjectInfoResponseData} from "../../../types/admin";
import logo from '../../../assets/covi_logo_white.png';
import {
    adminProjectInfoAPI,
    adminProjectSampleAPI,
    adminProjectWorkerAPI,
    adminWorkerAmountAPI
} from "../../../services/admin";
import {UserGroupIcon} from "@heroicons/react/20/solid";
import {useNavigate} from "react-router-dom";
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";

interface AdminProjectInfoProps {
    projectId: number;
}

//어드민 프로젝트 정보
const AdminProjectInfo: React.FC<AdminProjectInfoProps> = ({projectId}) => {
    const [data, setData] = React.useState<AdminProjectInfoResponseData | null>(null);
    const [sample, setSample] = React.useState<string>();
    const [loading, setLoading] = React.useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [workerAmount, setWorkerAmount] = useState<number>(0);
    const [maxAmount, setMaxAmount] = useState<number>(0);
    const navigate = useNavigate();
    const {showAlert} = useAlert();

    //프로젝트 정보 수정 페이지 이동
    const handleEdit = () => {
        navigate(`/admin/projects/${projectId}/edit`);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //어드민 프로젝트 정보 조회 API
            const response = await adminProjectInfoAPI(projectId);
            setData(response);
            //샘플 데이터 조회 API
            const file = await adminProjectSampleAPI(projectId);
            //파일 내부 URL 생성
            const url = URL.createObjectURL(file);
            setSample(url);
            //해당 클라이언트 계약 작업자 수 조회 API
            const max = await adminWorkerAmountAPI(response.projectId);
            setMaxAmount(max);
        } catch (error) {
            console.error('Error fetching project:', error);
        } finally {
            setLoading(false);
        }
    };

    //할당 작업자 수 변경
    const handleWorkerChange = async () => {
        try {
            //프로젝트 할당 작업자 수 변경 API
            await adminProjectWorkerAPI({
                projectId: projectId,
                workerAmount: workerAmount
            });
            showAlert('작업자 수가 변경되었습니다.', "info");
            fetchData();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error updating worker amount:', error);
        }
    };

    //프로젝트 할당 작업자 수 변경 팝업 열기
    const handleModalOpen = () => {
        setWorkerAmount(data?.workerAmount || 0);
        setIsModalOpen(true);
    };

    //날짜 요일
    const formatDate = (date: Date | undefined, day: number | undefined) => {
        if (!date || day === undefined) return '-';
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    useEffect(() => {
        fetchData();
    }, [projectId]);

    useEffect(() => {
        if (data) {
            setWorkerAmount(data.workerAmount || 0);
        }
    }, [data]);

    //날짜 정보
    const dateInfo = [
        {
            label: "최초 생성일",
            value: formatDate(data?.createdAt, data?.createDay)
        },
        {
            label: "작업 시작일",
            value: formatDate(data?.startedAt, data?.startDay)
        },
        {
            label: "작업 종료일",
            value: formatDate(data?.deadline, data?.deadlineDay)
        }
    ];

    //프로젝트 정보
    const projectInfo = [
        {
            label: "진행 상태",
            value: data?.projectStatus?.toString() || '-'
        },
        {
            label: "데이터 수량",
            value: data?.dataAmount ? `${data.dataAmount.toLocaleString()}장` : '-'
        },
        {
            label: "가공 방식",
            value: data?.projectType?.toString() || '-'
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <h2 className={styles.title}>Project Information</h2>
                        <p className={styles.subtitle}>프로젝트 진행 정보</p>
                    </div>
                    {/*프로젝트 수정 버튼*/}
                    <Button
                        onClick={handleEdit}
                        className={styles.modiButton}
                        variant="primary"
                        size="small"
                    >
                        수정
                    </Button>
                </div>
                <div className={styles.infoContainer}>
                    <div className={styles.leftSection}>
                        <h3 className={styles.projectName}>{data?.projectName || '-'}</h3>
                        <div className={styles.progressBar}>
                            {/*프로젝트 작업률 바 그래프*/}
                            <h3 className={styles.projectNameMobile}>{data?.projectName || '-'}</h3>
                            <span className={styles.label}>작업 달성률</span>
                            <svg viewBox="0 0 200 30">
                                <rect
                                    x="0"
                                    y="5"
                                    width="200"
                                    height="20"
                                    rx="2"
                                    fill="#E0E0E0"
                                />
                                <rect
                                    x="0"
                                    y="5"
                                    width={(data?.progress || 0) * 2}
                                    height="20"
                                    rx="2"
                                    fill="#002387"
                                />
                                <text
                                    x={(data?.progress || 0)}
                                    y="18"
                                    textAnchor="middle"
                                    fill="#ffffff"
                                    fontSize="8"
                                    fontFamily="Arial"
                                >
                                    {`${data?.progress}%`}
                                </text>
                            </svg>
                        </div>
                        <div className={styles.workersContainer}>
                            <div className={styles.workerContainer}>
                                <div className={styles.headerSection}>
                                    <div className={styles.textContainer}>
                                        <div className={styles.iconContainer}>
                                            <UserGroupIcon className={styles.iconImage}/>
                                            <h1>작업자 수</h1>
                                        </div>
                                        <p>프로젝트 고용 인원</p>
                                    </div>
                                </div>
                                <div className={styles.bottomSection}>
                                    <div className={styles.countText}>
                                        {/*현재 할당된 작업자 수*/}
                                        <span className={styles.countNumber}>{data?.workerAmount}명</span>
                                    </div>
                                    {/*할당 작업자 수 변경 버튼*/}
                                    <button
                                        className={styles.addButton}
                                        onClick={handleModalOpen}
                                    >
                                        변경
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.imageContainer}>
                            {/*샘플 데이터*/}
                            {sample ? (
                                <img
                                    src={sample}
                                    alt="Project Preview"
                                    className={styles.projectImage}
                                    onError={(e) => {
                                        e.currentTarget.src = logo;
                                    }}
                                />
                            ) : (
                                <div className={styles.noImage}>No image available</div>
                            )}
                        </div>

                        {/*프로젝트 정보*/}
                        <div className={styles.infoGrid}>
                            <TextCard items={dateInfo}/>
                            <TextCard items={projectInfo}/>
                        </div>
                    </div>
                </div>
            </div>

            {/*작업자 수 변경 팝업*/}
            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>작업자 수 변경</h3>
                        {/*대상 클라이언트 계약 작업자 수*/}
                        <span>현재 계약 총 작업자 수: {maxAmount}</span>
                        {/*작업자 수 입력*/}
                        <input
                            type="number"
                            value={workerAmount === 0 ? '' : workerAmount}
                            onChange={(e) => setWorkerAmount(Number(e.target.value))}
                            min="0"
                            className={styles.workerInput}
                        />
                        <div className={styles.modalButtons}>
                            {/*작업자 수 저장 버튼*/}
                            <button
                                className={styles.saveButton}
                                onClick={handleWorkerChange}
                            >
                                저장
                            </button>
                            {/*취소 버튼*/}
                            <button
                                className={styles.cancelButton}
                                onClick={() => setIsModalOpen(false)}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProjectInfo;